<template>
  <div class="roi-rule-page">
    <div class="page-head">
      <div>
        <h2>腾讯广告 ROI 自动规则</h2>
        <span>按账号标签监控当天创意 ROI，自动暂停或开启动态创意</span>
      </div>
      <div class="head-actions">
        <el-button size="mini" icon="el-icon-refresh" :loading="ruleLoading || logLoading" @click="refreshAll">刷新</el-button>
        <el-button size="mini" icon="el-icon-edit" :disabled="!selectedRules.length" @click="openBatchDialog">批量修改</el-button>
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="openDialog()">新增规则</el-button>
      </div>
    </div>

    <section class="auth-panel">
      <div class="auth-main">
        <span class="auth-title">腾讯实名认证</span>
        <el-tag size="mini" :type="userTokenTagType()">{{ userTokenStatusText() }}</el-tag>
        <span class="muted">{{ userTokenStatus.message || '用于动态创意暂停/开启接口的 user_token' }}</span>
      </div>
      <div class="auth-actions">
        <span v-if="userTokenStatus.expiresAt" class="muted">
          过期时间：{{ formatTime(userTokenStatus.expiresAt) }}，剩余 {{ formatNumber(userTokenStatus.remainingDays || 0) }} 天
        </span>
        <el-button size="mini" icon="el-icon-refresh" :loading="userTokenLoading" @click="loadUserTokenStatus">刷新认证</el-button>
        <el-button type="primary" size="mini" icon="el-icon-key" :disabled="!userTokenStatus.authUrl" @click="openUserTokenAuth">
          {{ userTokenStatus.hasToken && !userTokenStatus.expired ? '重新认证' : '去认证' }}
        </el-button>
      </div>
    </section>

    <section class="query-panel">
      <el-form :inline="true" size="mini" @submit.native.prevent>
        <el-form-item label="关键词">
          <el-input
            v-model.trim="query.keyword"
            clearable
            placeholder="标签、备注、创意、原因"
            style="width: 260px"
            @keyup.enter.native="search"
          ></el-input>
        </el-form-item>
        <el-form-item label="动作">
          <el-select v-model="query.action" clearable placeholder="全部" style="width: 130px">
            <el-option label="开启" value="ENABLE"></el-option>
            <el-option label="暂停" value="DISABLE"></el-option>
            <el-option label="跳过" value="SKIP"></el-option>
            <el-option label="失败" value="ERROR"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="ruleLoading || logLoading" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
    </section>

    <section class="rule-panel">
      <div class="table-toolbar">
        <span>规则列表</span>
        <span class="muted">ROI = 小游戏广告变现金额 / 消耗；阈值按小数填写，例如 0.12。</span>
      </div>
      <el-table
        :data="rules"
        size="mini"
        border
        stripe
        v-loading="ruleLoading"
        :header-cell-style="{background:'#f8fafc', color:'#303133'}"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="44"></el-table-column>
        <el-table-column prop="business_alias" label="标签" min-width="220" show-overflow-tooltip></el-table-column>
        <el-table-column prop="pause_roi_threshold" label="低于暂停" width="110">
          <template slot-scope="{ row }">{{ formatDecimal(row.pause_roi_threshold || row.roi_threshold) }}</template>
        </el-table-column>
        <el-table-column prop="enable_roi_threshold" label="高于开启" width="110">
          <template slot-scope="{ row }">{{ formatDecimal(row.enable_roi_threshold || row.roi_threshold) }}</template>
        </el-table-column>
        <el-table-column prop="min_cost_yuan" label="最低消耗" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.min_cost_yuan) }}</template>
        </el-table-column>
        <el-table-column prop="wait_minutes" label="开启后等待" width="120">
          <template slot-scope="{ row }">{{ formatNumber(row.wait_minutes) }} 分钟</template>
        </el-table-column>
        <el-table-column prop="enabled" label="状态" width="160">
          <template slot-scope="{ row }">
            <div class="status-switch-cell">
              <el-switch
                :value="Number(row.enabled) === 1"
                active-text="启用"
                inactive-text="停用"
                @change="value => toggleRule(row, value)"
              ></el-switch>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="last_run_at" label="最近执行时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.last_run_at) }}</template>
        </el-table-column>
        <el-table-column prop="matched_accounts" label="命中账户数" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.matched_accounts || 0) }}</template>
        </el-table-column>
        <el-table-column prop="matched_creatives" label="命中创意数" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.matched_creatives || 0) }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openDialog(row)">编辑</el-button>
            <el-button type="text" size="mini" class="danger-link" @click="deleteRule(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-row">
        <span>共 {{ rulePage.total }} 条规则</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="rulePage.page"
          :page-size="rulePage.pageSize"
          :page-sizes="[20, 50, 100]"
          :total="rulePage.total"
          @size-change="handleRuleSizeChange"
          @current-change="handleRuleCurrentChange"
        ></el-pagination>
      </div>
    </section>

    <section class="rule-panel">
      <div class="table-toolbar">
        <span>执行日志</span>
        <div class="toolbar-actions">
          <el-date-picker
            v-model="logDateRange"
            type="daterange"
            size="mini"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
            @change="handleLogDateChange"
          ></el-date-picker>
          <el-button size="mini" icon="el-icon-download" :loading="logExporting" @click="exportLogs">导出Excel</el-button>
          <el-button size="mini" icon="el-icon-refresh" :loading="logLoading" @click="loadLogs">刷新日志</el-button>
        </div>
      </div>
      <el-table
        :data="logs"
        size="mini"
        border
        stripe
        :height="420"
        v-loading="logLoading"
        :header-cell-style="{background:'#f8fafc', color:'#303133'}"
      >
        <el-table-column prop="created_at" label="执行时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column prop="business_alias" label="标签" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="account_id" label="账号ID" width="120"></el-table-column>
        <el-table-column prop="adgroup_id" label="营销单元ID" width="130"></el-table-column>
        <el-table-column prop="dynamic_creative_id" label="创意ID" width="130"></el-table-column>
        <el-table-column prop="dynamic_creative_name" label="创意名称" min-width="220" show-overflow-tooltip></el-table-column>
        <el-table-column prop="cost_yuan" label="消耗" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.cost_yuan) }}</template>
        </el-table-column>
        <el-table-column prop="revenue_yuan" label="收入" width="110">
          <template slot-scope="{ row }">{{ formatNumber(row.revenue_yuan) }}</template>
        </el-table-column>
        <el-table-column prop="roi_value" label="ROI" width="100">
          <template slot-scope="{ row }">{{ formatDecimal(row.roi_value) }}</template>
        </el-table-column>
        <el-table-column prop="threshold_value" label="阈值" width="100">
          <template slot-scope="{ row }">{{ formatDecimal(row.threshold_value) }}</template>
        </el-table-column>
        <el-table-column prop="action" label="动作" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="actionType(row.action)">{{ actionText(row.action) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="success" label="结果" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="Number(row.success) === 1 ? 'success' : 'danger'">
              {{ Number(row.success) === 1 ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="原因" min-width="220" show-overflow-tooltip></el-table-column>
      </el-table>
      <div class="pagination-row">
        <span>共 {{ logPage.total }} 条日志</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="logPage.page"
          :page-size="logPage.pageSize"
          :page-sizes="[30, 50, 100, 200]"
          :total="logPage.total"
          @size-change="handleLogSizeChange"
          @current-change="handleLogCurrentChange"
        ></el-pagination>
      </div>
    </section>

    <el-dialog
      :title="editingRule ? '编辑 ROI 自动规则' : '新增 ROI 自动规则'"
      :visible.sync="dialogVisible"
      width="520px"
      append-to-body
    >
      <el-form :model="form" label-width="130px" size="small">
        <el-form-item label="标签">
          <el-select
            v-model="form.businessAlias"
            filterable
            remote
            allow-create
            default-first-option
            :remote-method="searchLabels"
            :loading="labelLoading"
            placeholder="输入标签前缀或搜索已有标签"
            style="width: 100%"
          >
            <el-option v-for="label in labels" :key="label" :label="label" :value="label"></el-option>
          </el-select>
          <div class="form-hint">保存一条标签前缀规则；例如填写“标签A”，会匹配“标签A”后面带任意字符的账号标签。</div>
        </el-form-item>
        <el-form-item label="低于此 ROI 暂停">
          <el-input-number v-model="form.pauseRoiThreshold" :min="0" :step="0.01" :precision="4" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="高于此 ROI 开启">
          <el-input-number v-model="form.enableRoiThreshold" :min="0" :step="0.01" :precision="4" style="width: 100%"></el-input-number>
          <div class="form-hint">开启阈值应大于或等于暂停阈值，中间区间不会自动开关。</div>
        </el-form-item>
        <el-form-item label="最低消耗">
          <el-input-number v-model="form.minCostYuan" :min="0" :step="10" :precision="2" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="开启后等待">
          <el-input-number v-model="form.waitMinutes" :min="0" :step="30" :precision="0" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="启用规则">
          <el-switch v-model="form.enabled"></el-switch>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="form.remark" type="textarea" :rows="3" maxlength="1000" show-word-limit></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="submitRule">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="批量修改 ROI 自动规则"
      :visible.sync="batchDialogVisible"
      width="560px"
      append-to-body
    >
      <div class="selected-count">已选择 {{ selectedRules.length }} 条规则。只会修改勾选的字段。</div>
      <el-form :model="batchForm" label-width="150px" size="small">
        <div class="batch-field-row">
          <el-checkbox v-model="batchFields.pauseRoiThreshold">修改低于暂停 ROI</el-checkbox>
          <el-input-number v-model="batchForm.pauseRoiThreshold" :disabled="!batchFields.pauseRoiThreshold" :min="0" :step="0.01" :precision="4"></el-input-number>
        </div>
        <div class="batch-field-row">
          <el-checkbox v-model="batchFields.enableRoiThreshold">修改高于开启 ROI</el-checkbox>
          <el-input-number v-model="batchForm.enableRoiThreshold" :disabled="!batchFields.enableRoiThreshold" :min="0" :step="0.01" :precision="4"></el-input-number>
        </div>
        <div class="batch-field-row">
          <el-checkbox v-model="batchFields.minCostYuan">修改最低消耗</el-checkbox>
          <el-input-number v-model="batchForm.minCostYuan" :disabled="!batchFields.minCostYuan" :min="0" :step="10" :precision="2"></el-input-number>
        </div>
        <div class="batch-field-row">
          <el-checkbox v-model="batchFields.waitMinutes">修改开启后等待</el-checkbox>
          <el-input-number v-model="batchForm.waitMinutes" :disabled="!batchFields.waitMinutes" :min="0" :step="30" :precision="0"></el-input-number>
        </div>
        <div class="batch-field-row">
          <el-checkbox v-model="batchFields.enabled">修改启用状态</el-checkbox>
          <el-switch v-model="batchForm.enabled" :disabled="!batchFields.enabled" active-text="启用" inactive-text="停用"></el-switch>
        </div>
        <div class="batch-field-row align-start">
          <el-checkbox v-model="batchFields.remark">修改备注</el-checkbox>
          <el-input v-model.trim="batchForm.remark" :disabled="!batchFields.remark" type="textarea" :rows="3" maxlength="1000" show-word-limit></el-input>
        </div>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="batchSaving" @click="submitBatchUpdate">保存修改</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'TencentAiRoiRules',
  data() {
    return {
      query: {
        keyword: '',
        action: ''
      },
      rules: [],
      logs: [],
      labels: [],
      labelLoading: false,
      ruleLoading: false,
      logLoading: false,
      logExporting: false,
      userTokenLoading: false,
      userTokenStatus: {},
      saving: false,
      batchSaving: false,
      dialogVisible: false,
      batchDialogVisible: false,
      editingRule: null,
      form: this.emptyForm(),
      selectedRules: [],
      logDateRange: [],
      batchFields: {
        pauseRoiThreshold: false,
        enableRoiThreshold: false,
        minCostYuan: false,
        waitMinutes: false,
        enabled: false,
        remark: false
      },
      batchForm: this.emptyBatchForm(),
      rulePage: {
        page: 1,
        pageSize: 50,
        total: 0
      },
      logPage: {
        page: 1,
        pageSize: 50,
        total: 0
      }
    }
  },
  mounted() {
    this.refreshAll()
  },
  methods: {
    emptyForm() {
      return {
        businessAlias: '',
        pauseRoiThreshold: 0.12,
        enableRoiThreshold: 0.12,
        minCostYuan: 100,
        waitMinutes: 120,
        enabled: true,
        remark: ''
      }
    },
    emptyBatchForm() {
      return {
        pauseRoiThreshold: 0.12,
        enableRoiThreshold: 0.12,
        minCostYuan: 100,
        waitMinutes: 120,
        enabled: true,
        remark: ''
      }
    },
    search() {
      this.rulePage.page = 1
      this.logPage.page = 1
      this.refreshAll()
    },
    resetQuery() {
      this.query = { keyword: '', action: '' }
      this.logDateRange = []
      this.rulePage.page = 1
      this.logPage.page = 1
      this.refreshAll()
    },
    async refreshAll() {
      await Promise.all([this.loadRules(), this.loadLogs(), this.loadLabels(), this.loadUserTokenStatus()])
    },
    async loadUserTokenStatus() {
      this.userTokenLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/user-token/status')
        if (response.data.success) {
          this.userTokenStatus = response.data.data || {}
        } else {
          this.userTokenStatus = { message: response.data.msg || '认证状态查询失败' }
        }
      } catch (error) {
        this.userTokenStatus = { message: '认证状态查询失败' }
      } finally {
        this.userTokenLoading = false
      }
    },
    openUserTokenAuth() {
      const url = this.userTokenStatus && this.userTokenStatus.authUrl
      if (!url) {
        this.$message.warning('授权链接暂不可用')
        return
      }
      window.open(url, '_blank')
      this.$message.info('完成腾讯认证后，回到本页刷新认证状态')
    },
    userTokenStatusText() {
      if (!this.userTokenStatus || !this.userTokenStatus.oauthTokenReady) return '未配置'
      if (!this.userTokenStatus.hasToken) return '未认证'
      if (this.userTokenStatus.expired) return '已过期'
      return '有效'
    },
    userTokenTagType() {
      if (!this.userTokenStatus || !this.userTokenStatus.oauthTokenReady) return 'info'
      if (!this.userTokenStatus.hasToken || this.userTokenStatus.expired) return 'danger'
      const hours = Number(this.userTokenStatus.remainingHours || 0)
      return hours <= 48 ? 'warning' : 'success'
    },
    handleSelectionChange(rows) {
      this.selectedRules = rows || []
    },
    async loadRules() {
      this.ruleLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/roi-rules', {
          params: {
            keyword: this.query.keyword,
            page: this.rulePage.page,
            pageSize: this.rulePage.pageSize
          }
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.rules = data.rows || []
          this.rulePage.total = Number(data.total || 0)
          this.rulePage.page = Number(data.page || this.rulePage.page)
          this.rulePage.pageSize = Number(data.pageSize || this.rulePage.pageSize)
        } else {
          this.rules = []
          this.$message.error(response.data.msg || '规则查询失败')
        }
      } catch (error) {
        this.rules = []
        this.$message.error('规则查询失败')
      } finally {
        this.ruleLoading = false
      }
    },
    async loadLabels(keyword = '') {
      this.labelLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/roi-rules/labels', {
          params: { keyword, limit: 50 }
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.labels = data.labels || []
        }
      } catch (error) {
        this.labels = []
      } finally {
        this.labelLoading = false
      }
    },
    searchLabels(keyword) {
      this.loadLabels(keyword)
    },
    selectAllLabels() {
      this.form.businessAlias = (this.labels || [])[0] || ''
    },
    clearSelectedLabels() {
      this.form.businessAlias = ''
    },
    openBatchDialog() {
      if (!this.selectedRules.length) {
        this.$message.warning('请先选择规则')
        return
      }
      this.batchFields = {
        pauseRoiThreshold: false,
        enableRoiThreshold: false,
        minCostYuan: false,
        waitMinutes: false,
        enabled: false,
        remark: false
      }
      this.batchForm = this.emptyBatchForm()
      this.batchDialogVisible = true
    },
    buildBatchPayload() {
      const payload = {
        ids: this.selectedRules.map(row => row.id).filter(Boolean)
      }
      if (this.batchFields.pauseRoiThreshold) payload.pauseRoiThreshold = this.batchForm.pauseRoiThreshold
      if (this.batchFields.enableRoiThreshold) payload.enableRoiThreshold = this.batchForm.enableRoiThreshold
      if (this.batchFields.minCostYuan) payload.minCostYuan = this.batchForm.minCostYuan
      if (this.batchFields.waitMinutes) payload.waitMinutes = this.batchForm.waitMinutes
      if (this.batchFields.enabled) payload.enabled = !!this.batchForm.enabled
      if (this.batchFields.remark) payload.remark = this.batchForm.remark
      return payload
    },
    async submitBatchUpdate() {
      const payload = this.buildBatchPayload()
      if (Object.keys(payload).length <= 1) {
        this.$message.warning('请选择要修改的字段')
        return
      }
      if (payload.pauseRoiThreshold !== undefined && payload.enableRoiThreshold !== undefined &&
        Number(payload.enableRoiThreshold) < Number(payload.pauseRoiThreshold)) {
        this.$message.warning('高于开启 ROI 不能低于低于暂停 ROI')
        return
      }
      this.batchSaving = true
      try {
        const response = await this.$axios.post('/tencent-ai/roi-rules/batch-update', payload)
        if (response.data.success) {
          const data = response.data.data || {}
          this.$message.success('已批量修改 ' + (data.updated || 0) + ' 条规则')
          this.batchDialogVisible = false
          this.refreshAll()
        } else {
          this.$message.error(response.data.msg || '批量修改失败')
        }
      } catch (error) {
        this.$message.error('批量修改失败')
      } finally {
        this.batchSaving = false
      }
    },
    async loadLogs() {
      this.logLoading = true
      try {
        const response = await this.$axios.get('/tencent-ai/roi-rules/logs', {
          params: this.buildLogParams(true)
        })
        if (response.data.success) {
          const data = response.data.data || {}
          this.logs = data.rows || []
          this.logPage.total = Number(data.total || 0)
          this.logPage.page = Number(data.page || this.logPage.page)
          this.logPage.pageSize = Number(data.pageSize || this.logPage.pageSize)
        } else {
          this.logs = []
          this.$message.error(response.data.msg || '日志查询失败')
        }
      } catch (error) {
        this.logs = []
        this.$message.error('日志查询失败')
      } finally {
        this.logLoading = false
      }
    },
    buildLogParams(withPage) {
      const params = {
        keyword: this.query.keyword,
        action: this.query.action
      }
      if (Array.isArray(this.logDateRange) && this.logDateRange.length === 2) {
        params.start = this.logDateRange[0]
        params.end = this.logDateRange[1]
      }
      if (withPage) {
        params.page = this.logPage.page
        params.pageSize = this.logPage.pageSize
      }
      return params
    },
    handleLogDateChange() {
      this.logPage.page = 1
      this.loadLogs()
    },
    async exportLogs() {
      this.logExporting = true
      try {
        const response = await this.$axios.get('/tencent-ai/roi-rules/logs/export', {
          params: this.buildLogParams(false),
          responseType: 'blob'
        })
        this.downloadBlob(response, this.exportLogFileName())
      } catch (error) {
        this.$message.error('日志导出失败')
      } finally {
        this.logExporting = false
      }
    },
    exportLogFileName() {
      const range = Array.isArray(this.logDateRange) && this.logDateRange.length === 2
        ? this.logDateRange[0] + '_' + this.logDateRange[1]
        : this.formatDate(new Date())
      return '腾讯广告ROI自动规则日志_' + range + '.xlsx'
    },
    downloadBlob(response, fallbackName) {
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = this.downloadFileName(response, fallbackName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    downloadFileName(response, fallbackName) {
      const disposition = response && response.headers ? (response.headers['content-disposition'] || response.headers['Content-Disposition']) : ''
      const utf8Match = disposition && disposition.match(/filename\*=UTF-8''([^;]+)/i)
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(utf8Match[1])
        } catch (error) {
          return utf8Match[1]
        }
      }
      return fallbackName
    },
    openDialog(row) {
      this.editingRule = row || null
      this.form = row
        ? {
            businessAlias: row.business_alias,
            pauseRoiThreshold: Number(row.pause_roi_threshold || row.roi_threshold || 0),
            enableRoiThreshold: Number(row.enable_roi_threshold || row.roi_threshold || 0),
            minCostYuan: Number(row.min_cost_yuan || 100),
            waitMinutes: Number(row.wait_minutes || 120),
            enabled: Number(row.enabled) === 1,
            remark: row.remark || ''
          }
        : this.emptyForm()
      this.dialogVisible = true
      if (row && row.business_alias && this.labels.indexOf(row.business_alias) < 0) {
        this.labels.unshift(row.business_alias)
      }
      if (!this.labels.length) {
        this.loadLabels()
      }
    },
    selectedBusinessAliases() {
      const value = this.form.businessAlias
      const text = Array.isArray(value) ? value[0] : value
      return [String(text || '').trim()].filter(Boolean)
    },
    async submitRule() {
      const aliases = this.selectedBusinessAliases()
      if (!aliases.length) {
        this.$message.warning('请选择标签')
        return
      }
      if (Number(this.form.enableRoiThreshold) < Number(this.form.pauseRoiThreshold)) {
        this.$message.warning('高于开启 ROI 不能低于低于暂停 ROI')
        return
      }
      this.saving = true
      const basePayload = {
        pauseRoiThreshold: this.form.pauseRoiThreshold,
        enableRoiThreshold: this.form.enableRoiThreshold,
        minCostYuan: this.form.minCostYuan,
        waitMinutes: this.form.waitMinutes,
        enabled: !!this.form.enabled,
        remark: this.form.remark
      }
      try {
        if (this.editingRule) {
          const response = await this.$axios.put('/tencent-ai/roi-rules/' + this.editingRule.id, Object.assign({}, basePayload, {
            businessAlias: aliases[0]
          }))
          if (!response.data.success) {
            this.$message.error(response.data.msg || '保存失败')
            return
          }
        } else {
          const response = await this.$axios.post('/tencent-ai/roi-rules', Object.assign({}, basePayload, {
            businessAlias: aliases[0]
          }))
          if (!response.data.success) {
            this.$message.error(response.data.msg || '保存失败')
            return
          }
        }
        this.$message.success('规则已保存')
        this.dialogVisible = false
        this.refreshAll()
      } catch (error) {
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    async toggleRule(row, enabled) {
      try {
        const response = await this.$axios.post('/tencent-ai/roi-rules/' + row.id + '/toggle', { enabled })
        if (response.data.success) {
          row.enabled = enabled ? 1 : 0
          this.$message.success(enabled ? '规则已启用' : '规则已停用')
          this.loadLogs()
        } else {
          this.$message.error(response.data.msg || '切换失败')
          this.loadRules()
        }
      } catch (error) {
        this.$message.error('切换失败')
        this.loadRules()
      }
    },
    async deleteRule(row) {
      try {
        await this.$confirm('确认删除该 ROI 自动规则？', '删除规则', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消'
        })
      } catch (error) {
        return
      }
      try {
        const response = await this.$axios.delete('/tencent-ai/roi-rules/' + row.id)
        if (response.data.success) {
          this.$message.success('规则已删除')
          this.refreshAll()
        } else {
          this.$message.error(response.data.msg || '删除失败')
        }
      } catch (error) {
        this.$message.error('删除失败')
      }
    },
    handleRuleSizeChange(value) {
      this.rulePage.pageSize = value
      this.rulePage.page = 1
      this.loadRules()
    },
    handleRuleCurrentChange(value) {
      this.rulePage.page = value
      this.loadRules()
    },
    handleLogSizeChange(value) {
      this.logPage.pageSize = value
      this.logPage.page = 1
      this.loadLogs()
    },
    handleLogCurrentChange(value) {
      this.logPage.page = value
      this.loadLogs()
    },
    actionText(action) {
      const map = {
        ENABLE: '开启',
        DISABLE: '暂停',
        SKIP: '跳过',
        ERROR: '失败'
      }
      return map[String(action || '').toUpperCase()] || action || '-'
    },
    actionType(action) {
      const upper = String(action || '').toUpperCase()
      if (upper === 'ENABLE') return 'success'
      if (upper === 'DISABLE') return 'warning'
      if (upper === 'ERROR') return 'danger'
      return 'info'
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) return String(value)
      return numberValue.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    formatDecimal(value) {
      if (value === null || value === undefined || value === '') return '-'
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) return String(value)
      return numberValue.toLocaleString('zh-CN', { maximumFractionDigits: 4 })
    },
    formatTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').replace(/(\d{2}:\d{2}:\d{2})\.\d+\b/, '$1').replace(/\.0+\b/, '')
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return year + '-' + month + '-' + day
    }
  }
}
</script>

<style scoped>
.roi-rule-page {
  min-height: 100%;
  padding: 12px 14px 18px;
  background: #f5f7fb;
  color: #303133;
  box-sizing: border-box;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.page-head h2 {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
}

.page-head span,
.muted {
  color: #8c939d;
  font-size: 12px;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.query-panel,
.auth-panel,
.rule-panel {
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.auth-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 10px 12px;
}

.auth-main,
.auth-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.auth-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.query-panel {
  padding: 10px 12px 0;
}

.rule-panel {
  margin-top: 10px;
  padding: 8px;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  color: #606266;
  font-size: 12px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  color: #606266;
  font-size: 12px;
}

.danger-link {
  color: #f56c6c;
}

.form-hint {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}

.label-quick-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
  line-height: 18px;
}

.selected-label-count {
  color: #909399;
  font-size: 12px;
}

.selected-count {
  margin-bottom: 12px;
  color: #606266;
  font-size: 13px;
}

.status-switch-cell {
  min-width: 128px;
  white-space: nowrap;
}

.batch-field-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.batch-field-row.align-start {
  align-items: flex-start;
}

.batch-field-row .el-input-number,
.batch-field-row .el-textarea {
  width: 100%;
}

::v-deep .el-table {
  font-size: 12px;
}

::v-deep .el-table .cell {
  white-space: nowrap;
}

@media (max-width: 760px) {
  .page-head,
  .auth-panel,
  .pagination-row,
  .table-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .auth-main,
  .auth-actions {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
